import { DataTexture, LinearFilter, LinearMipmapLinearFilter, RepeatWrapping } from 'three/webgpu'

/** Tileable, deterministic fractal detail baked once on the CPU, mipmapped by
 * the GPU. Independent channels provide ripple slopes and broken foam patches.
 * Replaces many per-pixel MaterialX noise evaluations on tablet GPUs. */
export function createOceanDetailTexture(): DataTexture {
  const size = 128
  const bytes = new Uint8Array(size * size * 4)
  const hash = (x: number, y: number, channel: number) => {
    let n = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(channel + 1, 1274126177)
    n = Math.imul(n ^ (n >>> 13), 1274126177)
    return ((n ^ (n >>> 16)) >>> 0) / 4294967295
  }
  const smooth = (v: number) => v * v * (3 - 2 * v)
  const mix = (a: number, b: number, t: number) => a + (b - a) * t
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    for (let channel = 0; channel < 3; channel++) {
      let sum = 0, weight = 0
      for (let octave = 0; octave < 5; octave++) {
        const period = 4 << octave, amplitude = 1 / (1 << octave)
        const u = x / size * period, v = y / size * period
        const ix = Math.floor(u), iy = Math.floor(v), fx = smooth(u - ix), fy = smooth(v - iy)
        const cell = (dx: number, dy: number) => hash((ix + dx) % period, (iy + dy) % period, channel)
        sum += mix(mix(cell(0,0),cell(1,0),fx),mix(cell(0,1),cell(1,1),fx),fy) * amplitude
        weight += amplitude
      }
      bytes[(y * size + x) * 4 + channel] = Math.round(255 * sum / weight)
    }
    bytes[(y * size + x) * 4 + 3] = 255
  }
  const detail = new DataTexture(bytes, size, size)
  detail.name = 'Baked ocean ripples and foam'
  detail.wrapS = detail.wrapT = RepeatWrapping
  detail.magFilter = LinearFilter
  detail.minFilter = LinearMipmapLinearFilter
  detail.generateMipmaps = true
  detail.needsUpdate = true
  return detail
}
